import React, {useState} from 'react';
import {useAuth0} from '@auth0/auth0-react';
import {useNavigate} from 'react-router-dom';
import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import {getAppConfig} from 'src/utils/config';
import CreationFlowBar from 'src/components/CreationFlowBar';
import {Error as ErrorComponent} from 'src/components/Error';
import Step0 from '../../rna/steps/Step0';
import {PeakParameters} from './PeakParameters';
import type {ChipExperiment} from 'src/types/experiment';
import {createAnalysis, startAnalysis} from 'src/utils/bff-communication';

interface Step {
    name: string;
    caption: string;
    description: string;
    component: React.ReactNode;
    icon: React.ReactNode;
}

const FormContainer = styled(Box)({
    flex: 5,
    display: 'flex',
    flexDirection: 'column',
    width: '990px',
    padding: '1.5% 2%',
    gap: 10
});

interface CreateDefaultChipAnalysisProps {
    experiment: ChipExperiment;
    samples: any[];
}

/**
 * Component to create a default ChIP analysis. The default analysis will
 * perform fastq trimming, alignment, peak calling and annotation, and run QC
 * reports.
 *
 * This component defines the steps for the analysis creation flow. This
 * component handles contacting the BFF to actually create and start the
 * analysis.
 */
export const CreateDefaultChipAnalysis = ({experiment, samples}: CreateDefaultChipAnalysisProps) => {
    const {apiHost} = getAppConfig();
    const {getAccessTokenSilently} = useAuth0();
    const navigate = useNavigate();
    // Note that analysis_count comes as a string from BFF.
    // Explicitly parsing because don't like `==`.
    const isFirstAnalysis = parseInt(experiment?.analysis_count) === 0;
    const [formData, setFormData] = useState<ChipFormData>({
        type: 'ChIP Seq Default',
        name: isFirstAnalysis ? 'Default ChIP Analysis' : '',
        description: isFirstAnalysis
            ? 'This default analysis will perform fastq trimming, alignment, peak calling, sample annotation, and run QC reports.'
            : '',
        default_chip_analysis: {
            peak_calling_qvalue: 0.05,
            peak_calling_type: 'narrow_peaks',
            peak_annotation_tss_region_upstream: 3000,
            peak_annotation_tss_region_downstream: 3000,
            peak_annotation_level: 'transcript',
            peak_annotation_overlap: 'tss',
            peak_annotation_genomic_annotation_priority: ['Promoter', '5UTR', '3UTR', 'Exon', 'Intron', 'Downstream', 'Intergenic']
        }
    });
    const [ableToSave, setAbleToSave] = useState(false);
    const [error, setError] = useState<string>('');
    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!experiment) {
        return <ErrorComponent message="Experiment data is not available." />;
    }

    const handleContinueClick = async (stepIndex: number) => {
        if (stepIndex < steps.length - 1) {
            setStep(stepIndex + 1);
        } else {
            console.log('Final step reached, ready to save analysis:', formData);
            setIsSubmitting(true);
            try {
                const accessToken = await getAccessTokenSilently();
                const analysis = await createAnalysis(experiment, formData, apiHost, accessToken, samples);
                const _startresponse = await startAnalysis(experiment.id, analysis.id, formData, apiHost, accessToken);
                navigate(`/experiment/${experiment.id}`);
            } catch (err) {
                console.error('Error creating analysis:', err);
                setError(`${err instanceof Error ? err.message : 'Unknown error'}`);
                setIsSubmitting(false);
            }
        }
    };

    const steps: Step[] = [
        {
            name: 'Analysis details',
            caption: 'Name your analysis and provide a description',
            description: 'Name your analysis and optionally provide a description.',
            component: (
                <Step0
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setAbleToSave}
                    data={[]}
                    error={error}
                    setError={setError}
                />
            ),
            icon: <AssignmentOutlined sx={{fill: step === 0 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Edit analysis parameter settings',
            caption: 'Advanced configuration parameters for analysis.',
            description: 'Customize options for data analysis or use our default configuration.',
            component: <PeakParameters formData={formData} setFormData={setFormData} setValidForm={setAbleToSave} />,
            icon: <SettingsOutlined sx={{fill: step === 1 ? 'url(#linearColors)' : ''}} />
        }
    ];

    return (
        <>
            <CreationFlowBar title="SETUP NEW ANALYSIS" steps={steps} step={step} setStep={setStep} />
            <FormContainer>
                <Typography variant="headline" size="large">
                    {steps[step].name}
                </Typography>
                <Typography variant="body" size="medium" mb={2}>
                    {steps[step].description}
                </Typography>
                {steps[step].component}
                {error && <ErrorComponent message={error} />}
                <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={() => handleContinueClick(step)}
                    disabled={!ableToSave}
                    loading={isSubmitting}>
                    {step !== 1 ? 'Continue' : 'Run analysis'}
                </LoadingButton>
            </FormContainer>
        </>
    );
};
